import { useEffect, useState, useCallback } from "react";
import { ShieldAlert, RefreshCw, Loader2, CalendarClock, Phone, UserCheck, Wallet, AlertTriangle, Users, MapPin, Receipt, Undo2 } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { StatCard } from "@/components/common/StatCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { esCreditoFactoraje } from "@/lib/factoraje";
import { PagadorFactorajeSelect } from "@/components/creditos/PagadorFactorajeSelect";
import { mxn, fdate, formatearPlazo } from "@/lib/format";
import { formatDomicilio } from "@/lib/domicilio";
import { sugeridoDePago, ivaEtiquetaGc } from "@/lib/sugeridoPago";
import { hoyMX, useReversionFechaAnterior, creditoSinRevertibles } from "@/lib/fechaPago";
import { MoneyInput } from "@/components/common/MoneyInput";
import { DiferidosPanel } from "@/components/common/DiferidosPanel";
import { useAuth } from "@/context/AuthContext";
import { ExportExcelButton } from "@/components/common/ExportExcelButton";
import { ReciboPagoButton } from "@/components/common/ReciboPagoButton";
import { TooltipButton } from "@/components/common/Tooltips";
import { DespachosTab } from "@/components/cobranza/DespachosTab";
import { GestionesTab } from "@/components/cobranza/GestionesTab";
import { QuejasRedecoTab } from "@/components/cobranza/QuejasRedecoTab";
import { ProximosCobrosTab } from "@/components/cobranza/ProximosCobrosTab";
import { toast } from "sonner";

const BUCKET_COLOR = { "Al corriente": "green", "1-30": "amber", "31-60": "amber", "61-90": "red", "90+": "red" };
const BucketChip = ({ b }) => {
  const c = BUCKET_COLOR[b] || "amber";
  const map = { green: "bg-emerald-50 text-emerald-700", amber: "bg-amber-50 text-amber-700", red: "bg-red-50 text-red-700" };
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${map[c]}`}>{b || "—"}</span>;
};

// Resultados de gestión que legitiman aplicar cargo de gastos de cobranza (Art. 43 LTOSF).
// Debe coincidir con el backend (cobranza.py RESULTADOS_EFECTIVOS).
const RESULTADOS_EFECTIVOS = new Set(["contacto", "promesa", "negativa"]);

const isMobile = () =>
  typeof navigator !== "undefined" &&
  ((navigator.maxTouchPoints || 0) > 0 || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent || ""));

// Enlace a mapa: abre la app de mapas POR DEFECTO del dispositivo (esquema geo:) en móvil;
// respaldo a OpenStreetMap en navegador (escritorio). No fuerza Google Maps.
function buildMapHref(ficha) {
  const geo = ficha?.geo;
  const dir = [formatDomicilio(ficha?.domicilio), ficha?.entidad_federativa].filter(Boolean).join(", ");
  const label = ficha?.cliente || "Ubicación";
  if (geo && geo.lat != null && geo.lng != null) {
    const ll = `${geo.lat},${geo.lng}`;
    return isMobile()
      ? `geo:${ll}?q=${ll}(${encodeURIComponent(label)})`
      : `https://www.openstreetmap.org/?mlat=${geo.lat}&mlon=${geo.lng}#map=18/${geo.lat}/${geo.lng}`;
  }
  if (dir) {
    return isMobile()
      ? `geo:0,0?q=${encodeURIComponent(dir)}`
      : `https://www.openstreetmap.org/search?query=${encodeURIComponent(dir)}`;
  }
  return null;
}

export default function Cobranza() {
  const { can } = useAuth();
  const [tab, setTab] = useState("cartera");

  return (
    <div data-testid="cobranza-page-wrapper">
      <PageHeader title="Cobranza" subtitle="Cartera vencida · Próximos cobros · Despachos · Gestiones · Quejas y REDECO" />
      <div className="px-6 pt-4 md:px-8">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="flex-wrap">
            <TabsTrigger value="cartera" data-testid="tab-cartera-vencida">Cartera vencida</TabsTrigger>
            <TabsTrigger value="proximos" data-testid="tab-proximos-cobros">Próximos cobros</TabsTrigger>
            <TabsTrigger value="despachos" data-testid="tab-despachos">Despachos</TabsTrigger>
            <TabsTrigger value="gestiones" data-testid="tab-gestiones">Gestiones</TabsTrigger>
            <TabsTrigger value="redeco" data-testid="tab-quejas-redeco">Quejas y REDECO</TabsTrigger>
          </TabsList>
          <TabsContent value="cartera" data-testid="tab-content-cartera">
            <CarteraVencidaTabInner embedded />
          </TabsContent>
          <TabsContent value="proximos" data-testid="tab-content-proximos">
            <ProximosCobrosTab />
          </TabsContent>
          <TabsContent value="despachos" data-testid="tab-content-despachos">
            <DespachosTab can={can} />
          </TabsContent>
          <TabsContent value="gestiones" data-testid="tab-content-gestiones">
            <GestionesTab can={can} />
          </TabsContent>
          <TabsContent value="redeco" data-testid="tab-content-redeco">
            <QuejasRedecoTab can={can} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function CarteraVencidaTabInner({ embedded = false }) {
  const { can, user } = useAuth();
  const puedeGestion = can("cobranza", "crear");
  const puedeEditar = can("cobranza", "editar");
  const puedePago = can("pagos", "crear");

  const [tablero, setTablero] = useState(null);
  const [fichas, setFichas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [gestorFilter, setGestorFilter] = useState("todos");
  const [soloVencidos, setSoloVencidos] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [sel, setSel] = useState(null);
  const [gestiones, setGestiones] = useState([]);
  const [form, setForm] = useState({});
  const [pagoForm, setPagoForm] = useState({});
  const [pagos, setPagos] = useState([]);
  const [busy, setBusy] = useState(false);
  // Bloque 6 — feb 2026: cargos por gastos de cobranza + preview del cargo
  const [gcPreview, setGcPreview] = useState(null);      // GET /cobranza/{id}/gasto-preview
  const [gcCargos, setGcCargos] = useState({ cargos: [], total_pendiente: 0 });
  const [gcAplicar, setGcAplicar] = useState(false);      // toggle del bloque colapsable
  const [gcJustificacion, setGcJustificacion] = useState("");
  const [reversionOpen, setReversionOpen] = useState(null); // {cargo, motivo}
  // Sugerido UNIFICADO de la ficha (misma util que Cartera/Pagos, con la cuota vencida real)
  const [sugFicha, setSugFicha] = useState(null);
  const [credSug, setCredSug] = useState(null); // Build G: crédito de la ficha para el sugerido con fecha anterior
  const [credFx, setCredFx] = useState(null); // Build 3 · crédito de factoraje de la ficha (selector Pagador)
  // feb-2026: obligados solidarios del crédito seleccionado (denormalizados en `creditos`).
  // Cero cambios en `cobranza.py`: leemos del documento crédito al abrir la ficha.
  const [obligados, setObligados] = useState([]);
  const [referencias, setReferencias] = useState([]);

  // Build G: pago con fecha anterior → aviso y sugerido sin los cargos por pago tardío que se revertirán.
  const revCob = useReversionFechaAnterior(sel?.credito_id, pagoForm.fecha_pago, { activo: !!sel });
  const sugPago = revCob?.credito_ajustado && credSug
    ? sugeridoDePago(creditoSinRevertibles(credSug, revCob), credSug.tabla_amortizacion.find((cu) => cu.estatus !== "Pagada"))
    : sugFicha;
  useEffect(() => {
    if (!revCob || !sugFicha || !sugPago) return;
    const base = String(sugFicha.total), ajus = String(sugPago.total);
    setPagoForm((f) => ([base, ajus].includes(String(f.monto)) ? { ...f, monto: ajus } : f));
  }, [revCob]); // eslint-disable-line react-hooks/exhaustive-deps

  const cargar = useCallback(async () => {
    const [t, lst] = await Promise.all([
      api.get("/cobranza/tablero").then((r) => r.data),
      api.get("/cobranza/lista", { params: { solo_vencidos: soloVencidos ? 1 : 0 } }).then((r) => r.data),
    ]);
    setTablero(t);
    setFichas(lst);
  }, [soloVencidos]);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => { api.get("/usuarios").then((r) => setUsuarios(r.data)).catch(() => setUsuarios([])); }, []);

  const sincronizar = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/cobranza/sincronizar");
      toast.success(`Cartera sincronizada: ${data.procesados} créditos · IMOR ${data.imor}%`);
      await cargar();
    } catch { toast.error("No se pudo sincronizar la cartera"); }
    finally { setSyncing(false); }
  };

  const revisarPromesas = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/cobranza/promesas/revisar");
      if (data.incumplidas > 0) toast.warning(`${data.incumplidas} promesa(s) incumplida(s) → alertas generadas`);
      else toast.success(`Promesas revisadas: ${data.revisadas} (sin incumplimientos)`);
      await cargar();
    } catch { toast.error("No se pudieron revisar las promesas"); }
    finally { setBusy(false); }
  };

  const openFicha = async (row) => {
    setSel(row);
    setForm({ canal: "llamada", resultado: "contacto", promesa_fecha: "", promesa_monto: "", notas: "" });
    setPagoForm({ monto: "", fecha_pago: hoyMX(), medio_pago: "Transferencia SPEI", pago_efectivo: false, pago_tercero: false, nombre_tercero: "" });
    setCredSug(null);
    setGcAplicar(false);
    setGcJustificacion("");
    setSugFicha(null);
    const [g, pgs, prev, cargos, cred] = await Promise.all([
      api.get(`/cobranza/${row.credito_id}/gestiones`).then((r) => r.data),
      api.get("/pagos", { params: { credito_id: row.credito_id } }).then((r) => r.data).catch(() => []),
      api.get(`/cobranza/${row.credito_id}/gasto-preview`).then((r) => r.data).catch(() => null),
      api.get(`/cobranza/${row.credito_id}/cargos-cobranza`).then((r) => r.data).catch(() => ({ cargos: [], total_pendiente: 0 })),
      api.post(`/creditos/${row.credito_id}/recalcular-mora`).then((r) => r.data)
        .catch(() => api.get(`/creditos/${row.credito_id}`).then((r) => r.data).catch(() => null)),
    ]);
    setGestiones(g);
    setPagos(pgs);
    setGcPreview(prev);
    setGcCargos(cargos);
    // Obligados solidarios: leídos del documento `credito` (denormalizados). Puede ser
    // array vacío o inexistente si el producto no los pide o no se hidrataron aún.
    setObligados(Array.isArray(cred?.obligados_solidarios) ? cred.obligados_solidarios : []);
    setReferencias(Array.isArray(cred?.referencias_personales) ? cred.referencias_personales : []);
    setCredFx(esCreditoFactoraje(cred) ? cred : null);
    setCredSug(cred?.tabla_amortizacion?.length ? cred : null);
    // Sugerido UNIFICADO (jul-2026): util compartida con Cartera/Pagos (valores del backend).
    // recalcular-mora ADEMÁS devenga on-demand el cargo GC automático si ya aplica, por lo
    // que el desglose y el prellenado incluyen el cargo sin depender del CRON.
    const sug = cred?.tabla_amortizacion?.length
      ? sugeridoDePago(cred, cred.tabla_amortizacion.find((cu) => cu.estatus !== "Pagada"))
      : sugeridoDePago({
          ...row,
          total_moratorios_diferidos_pendientes: Number(cargos?.total_moratorio_diferido || 0),
          total_iva_moratorios_diferidos_pendientes: Number(cargos?.total_iva_moratorio_diferido || 0),
          total_gastos_cobranza_diferidos_pendientes: Number(cargos?.total_gc_diferido || 0),
          total_gastos_cobranza_pendientes: Number(cargos?.total_pendiente || 0),
          moratorios_diferidos: cargos?.moratorios_diferidos || [],
        }, null);
    setSugFicha(sug);
    if (sug.total > 0) {
      setPagoForm((f) => ({ ...f, monto: String(sug.total) }));
    }
  };

  const refrescarFicha = async (cid) => {
    await cargar();
    const lst = await api.get("/cobranza/lista", { params: { solo_vencidos: 0 } }).then((r) => r.data);
    const upd = lst.find((f) => f.credito_id === cid);
    if (upd) setSel(upd);
    const [g, pgs, prev, cargos, cred] = await Promise.all([
      api.get(`/cobranza/${cid}/gestiones`).then((r) => r.data),
      api.get("/pagos", { params: { credito_id: cid } }).then((r) => r.data).catch(() => []),
      api.get(`/cobranza/${cid}/gasto-preview`).then((r) => r.data).catch(() => null),
      api.get(`/cobranza/${cid}/cargos-cobranza`).then((r) => r.data).catch(() => ({ cargos: [], total_pendiente: 0 })),
      api.post(`/creditos/${cid}/recalcular-mora`).then((r) => r.data)
        .catch(() => api.get(`/creditos/${cid}`).then((r) => r.data).catch(() => null)),
    ]);
    setGestiones(g);
    setPagos(pgs);
    setGcPreview(prev);
    setGcCargos(cargos);
    setObligados(Array.isArray(cred?.obligados_solidarios) ? cred.obligados_solidarios : []);
    setReferencias(Array.isArray(cred?.referencias_personales) ? cred.referencias_personales : []);
    setCredFx(esCreditoFactoraje(cred) ? cred : null);
    setCredSug(cred?.tabla_amortizacion?.length ? cred : null);
    if (cred?.tabla_amortizacion?.length) {
      setSugFicha(sugeridoDePago(cred, cred.tabla_amortizacion.find((cu) => cu.estatus !== "Pagada")));
    }
  };

  const asignar = async (gestorId) => {
    try {
      await api.post(`/cobranza/${sel.credito_id}/asignar`, { gestor_id: gestorId === "ninguno" ? "" : gestorId });
      toast.success("Gestor asignado");
      await refrescarFicha(sel.credito_id);
    } catch { toast.error("No se pudo asignar el gestor"); }
  };

  const guardarGestion = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form, promesa_monto: Number(form.promesa_monto || 0),
      };
      // Bloque 2 (feb 2026): propagar el toggle de cargo por gastos de cobranza.
      // Solo se envía si el usuario lo activó (evita ruido en gestiones normales).
      if (gcAplicar) {
        payload.aplicar_gasto_cobranza = true;
        if (gcJustificacion.trim().length >= 4) {
          payload.justificacion_override = gcJustificacion.trim();
        }
      }
      const { data } = await api.post(`/cobranza/${sel.credito_id}/gestion`, payload);
      if (data.cargo_cobranza) {
        toast.success(
          `Gestión registrada · Cargo GC ${mxn(data.cargo_cobranza.monto)}${data.cargo_cobranza.iva ? ` + IVA ${mxn(data.cargo_cobranza.iva)}` : ""} aplicado`
        );
      } else if (data.cargo_cobranza_error) {
        toast.warning(`Gestión registrada, cargo NO aplicado: ${data.cargo_cobranza_error}`);
      } else {
        toast.success("Gestión registrada");
      }
      setForm({ canal: "llamada", resultado: "contacto", promesa_fecha: "", promesa_monto: "", notas: "" });
      setGcAplicar(false);
      setGcJustificacion("");
      await refrescarFicha(sel.credito_id);
    } catch { toast.error("No se pudo registrar la gestión"); }
    finally { setBusy(false); }
  };

  const revertirCargo = async () => {
    if (!reversionOpen?.cargo || (reversionOpen.motivo || "").trim().length < 4) {
      toast.error("El motivo de la reversión es obligatorio (mínimo 4 caracteres)");
      return;
    }
    setBusy(true);
    try {
      await api.post(
        `/cobranza/${sel.credito_id}/cargo-cobranza/${reversionOpen.cargo.id}/revertir`,
        { motivo: reversionOpen.motivo.trim() }
      );
      toast.success("Cargo revertido y póliza inversa generada");
      setReversionOpen(null);
      await refrescarFicha(sel.credito_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo revertir el cargo");
    } finally { setBusy(false); }
  };

  const registrarPago = async () => {
    if (!pagoForm.monto || Number(pagoForm.monto) <= 0) { toast.error("Captura el monto del pago"); return; }
    setBusy(true);
    try {
      const { data: pg } = await api.post("/pagos", {
        credito_id: sel.credito_id, numero_credito: sel.numero_credito, cliente: sel.cliente,
        cliente_id: sel.cliente_id, fecha_pago: pagoForm.fecha_pago || hoyMX(),
        monto: Number(pagoForm.monto), medio_pago: pagoForm.medio_pago,
        pago_efectivo: pagoForm.pago_efectivo, pago_tercero: pagoForm.pago_tercero,
        nombre_tercero: pagoForm.nombre_tercero,
        ...(credFx ? { pagador: pagoForm.pagador || "deudor" } : {}),
      });
      toast.success("Pago registrado y aplicado a la tabla");
      if (Number(pg?.remanente_generado) > 0) toast.info(`Remanente a favor del cliente (por devolver): ${mxn(pg.remanente_generado)}`);
      setPagoForm({ monto: "", fecha_pago: hoyMX(), medio_pago: "Transferencia SPEI", pago_efectivo: false, pago_tercero: false, nombre_tercero: "" });
      await api.post("/cobranza/sincronizar");
      await refrescarFicha(sel.credito_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar el pago");
    } finally { setBusy(false); }
  };

  const fichasFiltradas = gestorFilter === "todos" ? fichas
    : fichas.filter((f) => (gestorFilter === "ninguno" ? !f.gestor_id : f.gestor_id === gestorFilter));

  const COLS = [
    { key: "numero_credito", label: "Crédito" },
    { key: "cliente", label: "Cliente", render: (r) => (
      <div className="min-w-0">
        <span className="block truncate font-medium text-slate-800">{r.cliente}</span>
        {formatDomicilio(r.domicilio) && <span className="block truncate text-[11px] text-slate-400" title={formatDomicilio(r.domicilio)}><MapPin className="mr-0.5 inline h-3 w-3" />{formatDomicilio(r.domicilio)}</span>}
      </div>
    ) },
    { key: "periodicidad", label: "Periodicidad", render: (r) => r.periodicidad ? <StatusBadge value={r.periodicidad} /> : <span className="text-slate-400">—</span> },
    { key: "dias_atraso", label: "Días atraso", render: (r) => <span className="font-mono tabular-nums">{r.dias_atraso ?? 0}</span> },
    { key: "bucket", label: "Bucket", render: (r) => <BucketChip b={r.bucket} /> },
    { key: "monto_vencido", label: "Monto vencido", kind: "currency" },
    { key: "etapa_sugerida", label: "Etapa sugerida", render: (r) => r.etapa_sugerida ? <StatusBadge value={r.etapa_sugerida} /> : <span className="text-slate-400">—</span> },
    { key: "gestor", label: "Gestor", render: (r) => r.gestor || <span className="text-slate-400">Sin asignar</span> },
    { key: "promesa_pago", label: "Promesa", render: (r) => r.promesa_pago ? <span className="text-xs">{fdate(r.promesa_pago)} {r.promesa_estatus === "Incumplida" ? "⚠️" : ""}</span> : <span className="text-slate-400">—</span> },
    { key: "estatus_gestion", label: "Gestión", kind: "badge" },
  ];

  return (
    <div data-testid="cobranza-page">
      {!embedded && (
      <PageHeader title="Cobranza" subtitle="Cartera vencida autoalimentada desde la mora real (Fase 3)">
        <TooltipButton tooltip="Marca las promesas de pago vencidas como incumplidas"
          data-testid="revisar-promesas-btn" variant="outline" onClick={revisarPromesas} disabled={busy || !puedeEditar}>
          <CalendarClock className="mr-1.5 h-4 w-4" /> Revisar promesas
        </TooltipButton>
        <TooltipButton tooltip="Recalcula días de atraso y actualiza la cartera vencida"
          data-testid="sincronizar-btn" onClick={sincronizar} disabled={syncing || !puedeEditar} className="bg-accent hover:bg-forest-700">
          {syncing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Sincronizar cartera
        </TooltipButton>
        <ExportExcelButton endpoint="/exportar/gestion/cobranza" filename="cobranza.xlsx" testid="export-cobranza-btn" />
      </PageHeader>
      )}
      {embedded && (
        <div className="mb-4 flex flex-wrap items-center justify-end gap-2 pt-3">
          <TooltipButton tooltip="Marca las promesas de pago vencidas como incumplidas"
            data-testid="revisar-promesas-btn" variant="outline" onClick={revisarPromesas} disabled={busy || !puedeEditar}>
            <CalendarClock className="mr-1.5 h-4 w-4" /> Revisar promesas
          </TooltipButton>
          <TooltipButton tooltip="Recalcula días de atraso y actualiza la cartera vencida"
            data-testid="sincronizar-btn" onClick={sincronizar} disabled={syncing || !puedeEditar} className="bg-accent hover:bg-forest-700">
            {syncing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Sincronizar cartera
          </TooltipButton>
          <ExportExcelButton endpoint="/exportar/gestion/cobranza" filename="cobranza.xlsx" testid="export-cobranza-btn" />
        </div>
      )}

      <div className="space-y-6 p-6 md:p-8">
        {/* Indicadores */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard testid="kpi-imor" label="IMOR (cartera vencida)" value={`${tablero?.imor ?? 0}%`} icon="TrendingDown" accent="red" />
          <StatCard testid="kpi-saldo-vencido" label="Saldo vencido" value={mxn(tablero?.saldo_vencido)} icon="AlertTriangle" accent="amber" />
          <StatCard testid="kpi-creditos-vencidos" label="Créditos vencidos" value={tablero?.creditos_vencidos ?? 0} icon="FileWarning" accent="amber" />
          <StatCard testid="kpi-saldo-total" label="Saldo total cartera" value={mxn(tablero?.saldo_total)} icon="Wallet" accent="primary" />
        </div>

        {/* Buckets de aging */}
        <div>
          <h3 className="mb-2 font-heading text-sm font-semibold text-slate-700">Antigüedad de saldos (aging)</h3>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            {(tablero?.buckets || []).map((b) => (
              <div key={b.bucket} className="rounded-lg border border-slate-200 bg-white p-3" data-testid={`bucket-${b.bucket}`}>
                <div className="mb-1"><BucketChip b={b.bucket} /></div>
                <p className="font-heading text-xl font-semibold text-slate-900 tabular-nums">{b.count}</p>
                <p className="text-xs text-slate-500">{mxn(b.monto_vencido)} vencido</p>
              </div>
            ))}
          </div>
        </div>

        {/* Filtros + lista de trabajo */}
        <div className="flex flex-wrap items-center gap-3">
          <Label className="text-xs text-slate-500">Gestor</Label>
          <Select value={gestorFilter} onValueChange={setGestorFilter}>
            <SelectTrigger className="w-56" data-testid="filtro-gestor"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="ninguno">Sin asignar</SelectItem>
              {usuarios.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Switch checked={soloVencidos} onCheckedChange={setSoloVencidos} data-testid="solo-vencidos" />
            <Label className="text-xs text-slate-500">Solo vencidos</Label>
          </div>
        </div>

        <DataTable testid="tabla-cobranza" columns={COLS} rows={fichasFiltradas} onRowClick={openFicha}
          emptyLabel="Sin cartera para cobranza. Pulsa 'Sincronizar cartera'." />
      </div>

      {/* Ficha de cobranza */}
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {sel && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-heading">
                  <ShieldAlert className="h-5 w-5 text-accent" /> {sel.numero_credito} — {sel.cliente}
                </DialogTitle>
                <DialogDescription className="sr-only">Ficha de cobranza, gestiones y registro de pago</DialogDescription>
              </DialogHeader>

              <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
                {[
                  ["Periodicidad", sel.periodicidad || "—"],
                  ["Plazo", formatearPlazo(sel.plazo, sel.periodicidad)],
                  ["Días de atraso", sel.dias_atraso ?? 0],
                  ["Bucket", <BucketChip b={sel.bucket} key="b" />],
                  ["Monto vencido", mxn(sel.monto_vencido)],
                  ["Saldo insoluto", mxn(sel.saldo_insoluto)],
                  ["Etapa sugerida", sel.etapa_sugerida || "—"],
                  ["Estatus crédito", <StatusBadge value={sel.estatus_credito} key="e" />],
                  ["Promesa", sel.promesa_pago ? `${fdate(sel.promesa_pago)} (${sel.promesa_estatus || "Vigente"})` : "—"],
                  ["Gestor", sel.gestor || "Sin asignar"],
                  // Bloque 6 (feb 2026): acumulado de gastos de cobranza pendientes
                  gcCargos.total_pendiente > 0 || (gcCargos.cargos || []).length > 0
                    ? ["Gastos de cobranza",
                       <span key="gc-total" className="tabular-nums" data-testid="cobranza-gc-acumulado">
                         {mxn(gcCargos.total_pendiente)}
                         <span className="ml-1 text-[10px] font-normal text-slate-400">
                           ({(gcCargos.cargos || []).filter((c) => !c.revertido).length} cargos)
                         </span>
                       </span>]
                    : null,
                  // Diferimiento (jun-2026): moratorio congelado pendiente por cobrar
                  Number(gcCargos.total_moratorio_diferido || 0) > 0
                    ? ["Moratorio diferido",
                       <span key="mora-dif" className="tabular-nums text-sky-800" data-testid="cobranza-mora-diferida">
                         {mxn(gcCargos.total_moratorio_diferido)}
                       </span>]
                    : null,
                ].filter(Boolean).map(([l, v], i) => (
                  <div key={i} className="rounded-md border border-slate-200 bg-slate-50 p-2">
                    <p className="text-[10px] uppercase tracking-wide text-slate-500">{l}</p>
                    <p className="mt-0.5 text-sm font-semibold text-slate-800">{v}</p>
                  </div>
                ))}
              </div>

              {/* Domicilio del cliente + enlace a mapa (dato heredado del lead/expediente) */}
              <div className="mt-2.5 flex items-start justify-between gap-3 rounded-md border border-slate-200 bg-white p-3" data-testid="cobranza-domicilio">
                <div className="min-w-0">
                  <p className="text-[10px] uppercase tracking-wide text-slate-500">Domicilio</p>
                  {formatDomicilio(sel.domicilio) ? (
                    <p className="mt-0.5 text-sm text-slate-800" data-testid="cobranza-domicilio-texto">
                      <MapPin className="mr-1 inline h-3.5 w-3.5 text-accent" />
                      {formatDomicilio(sel.domicilio)}{sel.entidad_federativa ? `, ${sel.entidad_federativa}` : ""}
                    </p>
                  ) : (
                    <p className="mt-0.5 text-sm italic text-slate-400" data-testid="cobranza-domicilio-vacio">Sin domicilio registrado</p>
                  )}
                </div>
                {buildMapHref(sel) ? (
                  <a
                    data-testid="cobranza-ver-mapa"
                    href={buildMapHref(sel)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-accent/40 bg-accent/5 px-3 py-1.5 text-xs font-medium text-accent transition-colors hover:bg-accent/10"
                  >
                    <MapPin className="h-3.5 w-3.5" /> Ver en mapa
                  </a>
                ) : (
                  <button
                    data-testid="cobranza-ver-mapa-disabled"
                    disabled
                    className="inline-flex shrink-0 cursor-not-allowed items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-300"
                  >
                    <MapPin className="h-3.5 w-3.5" /> Ver en mapa
                  </button>
                )}
              </div>
              {/* Teléfono(s) del cliente — dato heredado del expediente. Sin captura extra:
                  se muestra el mismo `telefono` que ya envía /cobranza/lista. Si el cliente
                  tiene múltiples (celular/fijo/secundario) se listan todos; vacío → aviso claro. */}
              {(() => {
                const tels = [sel.telefono, sel.celular, sel.telefono_movil, sel.telefono_fijo, sel.telefono_secundario, sel.telefono_2]
                  .filter((t) => t && String(t).trim());
                return (
                  <div className="mt-2.5 flex items-start justify-between gap-3 rounded-md border border-slate-200 bg-white p-3" data-testid="cobranza-telefono">
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-wide text-slate-500">Teléfono</p>
                      {tels.length > 0 ? (
                        <div className="mt-0.5 space-y-0.5" data-testid="cobranza-telefono-lista">
                          {tels.map((t, i) => (
                            <p key={i} className="text-sm text-slate-800">
                              <Phone className="mr-1 inline h-3.5 w-3.5 text-accent" />
                              <a href={`tel:${String(t).replace(/[^0-9+]/g, "")}`} className="hover:underline">{t}</a>
                            </p>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-0.5 text-sm italic text-slate-400" data-testid="cobranza-telefono-vacio">Sin teléfono registrado</p>
                      )}
                    </div>
                  </div>
                );
              })()}
              {/* Obligados solidarios (feb-2026) — información de referencia para cobranza,
                  denormalizada desde `creditos.obligados_solidarios`. NO son sujetos obligados:
                  el bloque se OMITE por completo si el crédito no tiene obligados. */}
              {obligados.length > 0 && (
                <div className="mt-2.5 rounded-md border border-amber-200 bg-amber-50/60 p-3" data-testid="cobranza-obligados-solidarios">
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                    Obligado{obligados.length > 1 ? "s" : ""} solidario{obligados.length > 1 ? "s" : ""}
                    <span className="ml-1 font-normal text-amber-700">· Contacto adicional para cobranza</span>
                  </p>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    {obligados.map((o, i) => (
                      <div key={i} className="rounded border border-amber-300/60 bg-white p-2 text-sm" data-testid={`cobranza-obligado-${i + 1}`}>
                        <p className="text-[10px] font-semibold uppercase text-amber-700">Obligado {i + 1}</p>
                        <p className="font-medium text-slate-800" data-testid={`cobranza-obligado-${i + 1}-nombre`}>{o.nombre || "—"}</p>
                        <p className="text-xs text-slate-600" data-testid={`cobranza-obligado-${i + 1}-domicilio`}>{formatDomicilio(o.domicilio) || <span className="italic text-slate-400">Sin domicilio</span>}</p>
                        <p className="text-xs text-slate-700" data-testid={`cobranza-obligado-${i + 1}-telefono`}>
                          {o.telefono ? (
                            <>
                              <Phone className="mr-1 inline h-3.5 w-3.5 text-accent" />
                              <a href={`tel:${String(o.telefono).replace(/[^0-9+]/g, "")}`} className="hover:underline">{o.telefono}</a>
                            </>
                          ) : <span className="italic text-slate-400">Sin teléfono</span>}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {referencias.length > 0 && (
                <div className="mt-2.5 rounded-md border border-slate-200 bg-slate-50 p-3" data-testid="cobranza-referencias">
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                    Referencias personales
                    <span className="ml-1 font-normal text-slate-500">· Contacto adicional para cobranza</span>
                  </p>
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                    {referencias.map((r, i) => (
                      <div key={i} className="rounded border border-slate-300/60 bg-white p-2 text-sm" data-testid={`cobranza-referencia-${i + 1}`}>
                        <p className="text-[10px] font-semibold uppercase text-slate-600">Referencia {i + 1}</p>
                        <p className="font-medium text-slate-800" data-testid={`cobranza-referencia-${i + 1}-nombre`}>{r.nombre || "—"}</p>
                        <p className="text-xs text-slate-600" data-testid={`cobranza-referencia-${i + 1}-ocupacion`}>{r.ocupacion || <span className="italic text-slate-400">Sin ocupación</span>}</p>
                        <p className="text-xs text-slate-600" data-testid={`cobranza-referencia-${i + 1}-domicilio`}>{formatDomicilio(r.domicilio) || <span className="italic text-slate-400">Sin domicilio</span>}</p>
                        <p className="text-xs text-slate-700" data-testid={`cobranza-referencia-${i + 1}-telefono`}>
                          {r.telefono ? (
                            <>
                              <Phone className="mr-1 inline h-3.5 w-3.5 text-accent" />
                              <a href={`tel:${String(r.telefono).replace(/[^0-9+]/g, "")}`} className="hover:underline">{r.telefono}</a>
                            </>
                          ) : <span className="italic text-slate-400">Sin teléfono</span>}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <p className="text-[11px] text-slate-400">Días de atraso y monto vencido provienen de la mora real (tabla de amortización), no de captura manual.</p>

              {sel.es_grupal && sel.integrantes_mora?.length > 0 && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3" data-testid="cobranza-integrantes">
                  <h4 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-emerald-700">
                    <Users className="h-4 w-4" /> Mora por integrante · obligados solidarios
                  </h4>
                  <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                        <tr>
                          <th className="px-2 py-2">Integrante</th><th className="px-2 py-2">Rol</th>
                          <th className="px-2 py-2 text-right">Días atraso</th><th className="px-2 py-2">Bucket</th>
                          <th className="px-2 py-2 text-right">Monto vencido</th><th className="px-2 py-2 text-right">Saldo</th>
                          <th className="px-2 py-2">Estatus</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sel.integrantes_mora.map((it) => (
                          <tr key={it.cliente_id} className="border-t border-slate-100" data-testid={`cobranza-integrante-${it.cliente_id}`}>
                            <td className="px-2 py-1.5 font-medium text-slate-700">{it.cliente}</td>
                            <td className="px-2 py-1.5">{it.rol === "titular" ? "Titular" : "Obligado"}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{it.dias_atraso || 0}</td>
                            <td className="px-2 py-1.5"><BucketChip b={it.bucket} /></td>
                            <td className="px-2 py-1.5 text-right font-mono">{mxn(it.monto_vencido)}</td>
                            <td className="px-2 py-1.5 text-right font-mono">{mxn(it.saldo_insoluto)}</td>
                            <td className="px-2 py-1.5"><StatusBadge value={it.estatus || "Vigente"} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="mt-1.5 text-[11px] text-slate-400">El pago desde esta ficha se reparte sobre el grupo. Para pagar a un integrante específico, usa Cartera → detalle del crédito.</p>
                </div>
              )}

              {/* Asignar gestor */}
              <div className="rounded-lg border border-slate-200 p-3">
                <h4 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700"><UserCheck className="h-4 w-4 text-primary" /> Asignar gestor</h4>
                <Select value={sel.gestor_id || "ninguno"} onValueChange={asignar} disabled={!puedeEditar}>
                  <SelectTrigger className="w-full sm:w-72" data-testid="asignar-gestor"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ninguno">Sin asignar</SelectItem>
                    {usuarios.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Registrar gestión */}
              <div className="rounded-lg border border-slate-200 p-3">
                <h4 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700"><Phone className="h-4 w-4 text-primary" /> Registrar gestión</h4>
                <div className="grid grid-cols-2 gap-2.5">
                  <div>
                    <Label className="mb-1 block text-xs">Canal</Label>
                    <Select value={form.canal} onValueChange={(v) => setForm({ ...form, canal: v })}>
                      <SelectTrigger data-testid="gestion-canal"><SelectValue /></SelectTrigger>
                      <SelectContent>{["llamada", "WhatsApp", "visita", "email", "SMS"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-1 block text-xs">Resultado</Label>
                    <Select value={form.resultado} onValueChange={(v) => setForm({ ...form, resultado: v })}>
                      <SelectTrigger data-testid="gestion-resultado"><SelectValue /></SelectTrigger>
                      <SelectContent>{["contacto", "promesa", "sin contacto", "negativa", "ilocalizable"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {form.resultado === "promesa" && (
                    <>
                      <div><Label className="mb-1 block text-xs">Fecha de promesa</Label><Input type="date" data-testid="promesa-fecha" value={form.promesa_fecha} onChange={(e) => setForm({ ...form, promesa_fecha: e.target.value })} /></div>
                      <div><Label className="mb-1 block text-xs">Monto de promesa</Label><MoneyInput data-testid="promesa-monto" value={form.promesa_monto} onChange={(v) => setForm({ ...form, promesa_monto: v })} /></div>
                    </>
                  )}
                  <div className="col-span-2"><Label className="mb-1 block text-xs">Notas</Label><Input data-testid="gestion-notas" value={form.notas} onChange={(e) => setForm({ ...form, notas: e.target.value })} placeholder="Detalle de la gestión" /></div>
                </div>

                {/* Bloque 6 - feb 2026: aplicar cargo por gastos de cobranza ligado a esta gestión */}
                {gcPreview && gcPreview.gastos_cobranza > 0 && (
                  <div className="mt-3 rounded-md border border-orange-200 bg-orange-50/50 p-2.5"
                       data-testid="cobranza-gc-bloque">
                    <div className="flex items-center justify-between gap-2">
                      <Label className="flex items-center gap-2 text-xs font-semibold text-orange-900">
                        <Receipt className="h-3.5 w-3.5" /> Aplicar cargo de gastos de cobranza
                      </Label>
                      <Switch
                        data-testid="cobranza-gc-toggle"
                        checked={gcAplicar}
                        onCheckedChange={(v) => setGcAplicar(v)}
                        disabled={!gcPreview.elegible || !RESULTADOS_EFECTIVOS.has(form.resultado)}
                      />
                    </div>
                    {!gcPreview.elegible && (
                      <p className="mt-1 text-[11px] text-orange-800" data-testid="cobranza-gc-no-elegible">
                        {gcPreview.bucket === "Al corriente"
                          ? "El crédito está al corriente — no procede aplicar cargo."
                          : "Crédito no elegible para cargo (monto=0 o configuración inconsistente)."}
                      </p>
                    )}
                    {!RESULTADOS_EFECTIVOS.has(form.resultado) && gcPreview.elegible && (
                      <p className="mt-1 text-[11px] text-orange-800">
                        Solo con gestión efectiva (contacto, promesa o negativa). Cambia el resultado para habilitar.
                      </p>
                    )}
                    {gcAplicar && gcPreview.elegible && (
                      <div className="mt-2 space-y-1.5 text-[11px] text-slate-700">
                        <div className="grid grid-cols-2 gap-2 rounded bg-white/70 p-2 font-mono tabular-nums">
                          <div><span className="text-slate-500">Base:</span> {gcPreview.gastos_cobranza_tipo === "monto" ? "Monto fijo" : `${gcPreview.gastos_cobranza}% sobre capital vencido`}</div>
                          <div data-testid="cobranza-gc-monto"><span className="text-slate-500">Monto:</span> <b>{mxn(gcPreview.monto)}</b></div>
                          <div data-testid="cobranza-gc-iva"><span className="text-slate-500">IVA:</span> {gcPreview.causa_iva ? mxn(gcPreview.iva) : <span className="italic text-slate-400">no aplica</span>}</div>
                          <div><span className="text-slate-500">Bucket:</span> {gcPreview.bucket}</div>
                        </div>
                        {(gcPreview.requiere_override_ventana || gcPreview.requiere_override_cap_mes) && (
                          <div className="rounded bg-red-50 p-2 text-red-800" data-testid="cobranza-gc-warning">
                            <p className="mb-1 flex items-center gap-1 font-semibold">
                              <AlertTriangle className="h-3 w-3" />
                              {gcPreview.requiere_override_ventana
                                ? `Ya se aplicó un cargo hace ${gcPreview.horas_desde_ultimo.toFixed(1)}h (mínimo ${gcPreview.min_horas}h).`
                                : `Cap mensual alcanzado (${gcPreview.cargos_mes}/${gcPreview.max_por_mes}).`}
                            </p>
                            <Input
                              data-testid="cobranza-gc-justificacion"
                              value={gcJustificacion}
                              onChange={(e) => setGcJustificacion(e.target.value)}
                              placeholder="Justificación (mín. 4 caracteres) para forzar el cargo"
                              className="text-xs"
                            />
                          </div>
                        )}
                        <p className="text-[10px] italic text-slate-500">
                          Póliza automática: <b>Cargo 105.01 · Abono 4126-GC</b>{gcPreview.causa_iva ? " + IVA devengado 209.01" : ""}. Al pagarse, se traspasa a ingresos cobrados 4126-GC{gcPreview.causa_iva ? " y IVA 208.01" : ""}.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                <TooltipButton tooltip="Registrar una gestión (llamada, correo, visita, promesa)"
                  data-testid="guardar-gestion-btn" size="sm" className="mt-3" onClick={guardarGestion} disabled={busy || !puedeGestion}>
                  {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Guardar gestión
                </TooltipButton>
              </div>

              {/* Diferidos (jun-2026): mora congelada + GC diferidos + pago adelantado */}
              {(Number(gcCargos.total_moratorio_diferido || 0) > 0 || Number(gcCargos.total_gc_diferido || 0) > 0) && (
                <DiferidosPanel
                  creditoId={sel.credito_id}
                  moratoriosDiferidos={gcCargos.moratorios_diferidos || []}
                  cargosDiferidos={(gcCargos.cargos || []).filter((c) => c.diferido && !c.revertido)}
                  totalMora={gcCargos.total_moratorio_diferido}
                  totalGc={gcCargos.total_gc_diferido}
                  canPay={puedePago && !sel.es_grupal}
                  onPagado={() => refrescarFicha(sel.credito_id)}
                />
              )}

              {/* Registrar pago (reusa POST /pagos con guard PLD) */}
              <div className="rounded-lg border border-slate-200 p-3">
                <h4 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700"><Wallet className="h-4 w-4 text-accent" /> Registrar pago</h4>
                <div className="grid grid-cols-2 gap-2.5">
                  <div><Label className="mb-1 block text-xs">Fecha de pago</Label><Input type="date" data-testid="cobranza-pago-fecha" value={pagoForm.fecha_pago || ""} onChange={(e) => setPagoForm({ ...pagoForm, fecha_pago: e.target.value })} disabled={user?.role !== "admin_entidad"} />{user?.role !== "admin_entidad" && <p className="mt-1 text-[11px] text-slate-400">El pago se registra con la fecha de hoy. Solo el administrador puede capturar otra fecha.</p>}</div>
                  <div><Label className="mb-1 block text-xs">Monto</Label><MoneyInput data-testid="cobranza-pago-monto" value={pagoForm.monto} onChange={(v) => setPagoForm({ ...pagoForm, monto: v })} /></div>
                  <div>
                    <Label className="mb-1 block text-xs">Medio</Label>
                    <Select value={pagoForm.medio_pago} onValueChange={(v) => setPagoForm({ ...pagoForm, medio_pago: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{["Transferencia SPEI", "Efectivo", "Domiciliación", "Cheque", "Tarjeta"].map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2"><Switch checked={pagoForm.pago_efectivo} onCheckedChange={(v) => setPagoForm({ ...pagoForm, pago_efectivo: v })} data-testid="pago-efectivo" /><Label className="text-xs">Pago en efectivo</Label></div>
                  {credFx ? (
                    <PagadorFactorajeSelect className="col-span-2" credito={credFx} value={pagoForm.pagador}
                      nombreTercero={pagoForm.nombre_tercero}
                      onChange={(patch) => setPagoForm((f) => ({ ...f, ...patch, ...(patch.pagador ? { pago_tercero: patch.pagador === "tercero" } : {}) }))} />
                  ) : (
                    <>
                      <div className="flex items-center gap-2"><Switch checked={pagoForm.pago_tercero} onCheckedChange={(v) => setPagoForm({ ...pagoForm, pago_tercero: v })} data-testid="pago-tercero" /><Label className="text-xs">Pago de tercero</Label></div>
                      {pagoForm.pago_tercero && <div className="col-span-2"><Label className="mb-1 block text-xs">Nombre del tercero</Label><Input value={pagoForm.nombre_tercero} onChange={(e) => setPagoForm({ ...pagoForm, nombre_tercero: e.target.value })} /></div>}
                    </>
                  )}
                </div>
                {revCob?.aviso && (
                  <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] font-medium text-amber-900" data-testid="cobranza-pago-aviso-reversion">
                    {revCob.aviso}
                  </div>
                )}
                {sugPago && sugPago.total > 0
                  && (sugPago.moraCorriente > 0 || sugPago.ivaMoraCorriente > 0 || sugPago.difMora > 0
                      || sugPago.difGc > 0 || sugPago.gcActivos > 0) && (
                  <div className="mt-2 rounded-md border border-sky-200 bg-sky-50/60 px-3 py-2 text-[11px] text-sky-900" data-testid="cobranza-pago-sugerido">
                    <p className="mb-1 font-semibold">Sugerido para saldar el periodo:</p>
                    <div className="space-y-0.5 font-mono tabular-nums">
                      {sugPago.moraCorriente > 0 && (
                        <div className="flex justify-between" data-testid="cobranza-sugerido-mora-corriente">
                          <span>Intereses moratorios{sugPago.moraDiasCorriente ? ` (${sugPago.moraDiasCorriente} días de atraso)` : ""}{sugPago.proximaCuotaNo ? ` — cuota #${sugPago.proximaCuotaNo}` : ""}</span>
                          <span>{mxn(sugPago.moraCorriente)}</span>
                        </div>
                      )}
                      {sugPago.ivaMoraCorriente > 0 && (
                        <div className="flex justify-between" data-testid="cobranza-sugerido-iva-mora-corriente">
                          <span>+ IVA sobre intereses moratorios</span><span>{mxn(sugPago.ivaMoraCorriente)}</span>
                        </div>
                      )}
                      {(sugPago.difDetalle || []).map((m) => (
                        <div key={`dif-${m.cuota_no}`} className="flex justify-between" data-testid={`cobranza-sugerido-mora-${m.cuota_no}`}>
                          <span>+ Moratorios periodo anterior{m.dias_atraso ? ` (${m.dias_atraso} días de atraso)` : ""} — cuota #{m.cuota_no}</span>
                          <span>{mxn(m.pendiente)}</span>
                        </div>
                      ))}
                      {sugPago.difMoraIva > 0 && (
                        <div className="flex justify-between" data-testid="cobranza-sugerido-iva-mora">
                          <span>+ IVA sobre moratorios diferidos</span><span>{mxn(sugPago.difMoraIva)}</span>
                        </div>
                      )}
                      {sugPago.difGc > 0 && (
                        <div className="flex justify-between" data-testid="cobranza-sugerido-gc">
                          <span>+ {sugPago.gcEtiqueta} diferidos</span><span>{mxn(sugPago.difGc)}</span>
                        </div>
                      )}
                      {sugPago.gcActivos > 0 && (sugPago.gcDesglose ? (
                        <>
                          <div className="flex justify-between" data-testid="cobranza-sugerido-gc-activo">
                            <span>+ {sugPago.gcEtiqueta}{sugPago.moraDiasCorriente ? ` (${sugPago.moraDiasCorriente} días)` : ""}</span><span>{mxn(sugPago.gcDesglose.monto)}</span>
                          </div>
                          {sugPago.gcDesglose.iva > 0 && (
                            <div className="flex justify-between" data-testid="cobranza-sugerido-gc-iva">
                              <span>+ {ivaEtiquetaGc(sugPago.gcEtiqueta)}</span><span>{mxn(sugPago.gcDesglose.iva)}</span>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="flex justify-between" data-testid="cobranza-sugerido-gc-activo">
                          <span>+ {sugPago.gcEtiqueta} (cargos activos + IVA)</span><span>{mxn(sugPago.gcActivos)}</span>
                        </div>
                      ))}
                      <div className="flex justify-between" data-testid="cobranza-sugerido-cuota">
                        <span>+ Cuota del periodo{sugPago.esParcial ? " (pendiente)" : ""}</span><span>{mxn(sugPago.cuota)}</span>
                      </div>
                      {sugPago.cuotaComision > 0 && (
                        <div className="flex justify-between" data-testid="cobranza-sugerido-comision">
                          <span>+ Comisión de apertura de la cuota (+ IVA)</span><span>{mxn(sugPago.cuotaComision)}</span>
                        </div>
                      )}
                      <div className="mt-1 flex justify-between border-t border-sky-200 pt-1 font-semibold" data-testid="cobranza-sugerido-total">
                        <span>Total sugerido</span><span>{mxn(sugPago.total)}</span>
                      </div>
                    </div>
                    <p className="mt-1 font-sans text-[10.5px] text-sky-700/80">
                      La mora corre sobre el capital PENDIENTE real de cada cuota (sellado − pagado) — el capital nunca se incrementa por mora.
                    </p>
                  </div>
                )}
                <p className="mt-1 text-[11px] text-slate-400">Pago de tercero (Proveedor de Recursos) y efectivo ≥ $50,000 generan validación/alerta PLD automática.</p>
                <TooltipButton tooltip="Registrar el pago (salda primero diferidos congelados, luego interés→IVA→capital de la cuota)"
                  data-testid="cobranza-registrar-pago-btn" size="sm" variant="outline" className="mt-2" onClick={registrarPago} disabled={busy || !puedePago}>Registrar pago</TooltipButton>
              </div>

              {/* Pagos del crédito (descarga / comparte recibo PDF) */}
              <div className="rounded-lg border border-slate-200 p-3" data-testid="cobranza-pagos-credito">
                <h4 className="mb-2 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700"><Wallet className="h-4 w-4 text-accent" /> Pagos del crédito ({pagos.length})</h4>
                {pagos.length === 0 ? (
                  <p className="text-xs text-slate-400">Sin pagos registrados.</p>
                ) : (
                  <div className="space-y-1.5">
                    {pagos.map((p) => {
                      const cancelado = p.estatus === "Cancelado";
                      return (
                        <div key={p.id} className={`flex items-center justify-between rounded-md border border-slate-100 bg-slate-50 px-3 py-1.5 text-xs ${cancelado ? "opacity-60" : ""}`} data-testid={`cobranza-pago-row-${p.id}`}>
                          <div className="min-w-0">
                            <span className="font-mono text-[11px] text-slate-600">{p.recibo}</span>
                            <span className="ml-2 text-slate-500">{fdate(p.fecha_pago)}</span>
                            {cancelado && <span className="ml-2 font-medium text-red-500">Cancelado</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`font-mono ${cancelado ? "line-through text-slate-400" : "text-slate-700"}`}>{mxn(p.monto)}</span>
                            <ReciboPagoButton pago={p} size="icon" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Cargos por gastos de cobranza (Bloque 6 - feb 2026) */}
              {(gcCargos.cargos || []).length > 0 && (
                <div className="rounded-lg border border-orange-200 bg-orange-50/30 p-3" data-testid="cobranza-gc-lista">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="flex items-center gap-2 font-heading text-sm font-semibold text-orange-900">
                      <Receipt className="h-4 w-4" /> Cargos: {sugFicha?.gcEtiqueta || "Gastos de cobranza"} ({gcCargos.cargos.length})
                    </h4>
                    <span className="text-xs text-orange-800">
                      Pendiente: <b className="font-mono">{mxn(gcCargos.total_pendiente)}</b>
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {gcCargos.cargos.map((c) => {
                      const pagado = (Number(c.pagado_monto || 0) + Number(c.pagado_iva || 0));
                      const total = Number(c.monto || 0) + Number(c.iva || 0);
                      return (
                        <div key={c.id}
                             className={`flex items-center justify-between rounded-md border px-3 py-2 text-xs ${c.revertido ? "border-slate-200 bg-slate-50 opacity-60" : "border-orange-200 bg-white"}`}
                             data-testid={`cobranza-gc-cargo-${c.id}`}>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-mono text-slate-700">{fdate(c.fecha)}</span>
                              <span className="text-slate-500">{c.canal}/{c.resultado}</span>
                              <BucketChip b={c.bucket} />
                              {c.override && <span className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700">override</span>}
                              {c.revertido && <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">revertido</span>}
                            </div>
                            <div className="mt-0.5 text-slate-500">
                              Gestor: {c.gestor || "—"}
                              {c.notas ? ` · ${c.notas}` : ""}
                              {c.revertido && c.revertido_motivo ? ` · Reversión: ${c.revertido_motivo}` : ""}
                            </div>
                          </div>
                          <div className="flex shrink-0 items-center gap-2">
                            <div className="text-right">
                              <div className={`font-mono ${c.revertido ? "line-through text-slate-400" : "text-orange-900"}`}>
                                {mxn(c.monto)}{c.iva > 0 && ` + IVA ${mxn(c.iva)}`}
                              </div>
                              {pagado > 0 && (
                                <div className="text-[10px] text-emerald-600">
                                  Pagado: {mxn(pagado)} / {mxn(total)}
                                </div>
                              )}
                            </div>
                            {!c.revertido && pagado === 0 && puedeEditar && (
                              <TooltipButton
                                tooltip="Revertir este cargo (genera póliza inversa)"
                                data-testid={`cobranza-gc-revertir-${c.id}`}
                                variant="ghost" size="icon"
                                onClick={() => setReversionOpen({ cargo: c, motivo: "" })}
                              >
                                <Undo2 className="h-3.5 w-3.5 text-red-600" />
                              </TooltipButton>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <p className="mt-2 text-[11px] italic text-orange-800">
                    Cada cargo está ligado 1:1 a la gestión que lo motivó (trazabilidad Art. 43 LTOSF). El cobro se aplica en la prelación después del IVA de intereses y antes del capital.
                  </p>
                </div>
              )}

              {/* Bitácora de gestiones */}
              <div>
                <h4 className="mb-2 font-heading text-sm font-semibold text-slate-700">Bitácora de gestiones ({gestiones.length})</h4>
                <div className="space-y-1.5">
                  {gestiones.length === 0 && <p className="text-xs text-slate-400">Sin gestiones registradas.</p>}
                  {gestiones.map((g) => (
                    <div key={g.id} className={`rounded-md border px-3 py-2 text-xs ${g.automatica ? "border-amber-200 bg-amber-50" : "border-slate-100 bg-slate-50"}`} data-testid={`gestion-row-${g.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-700">{g.automatica && <AlertTriangle className="mr-1 inline h-3 w-3 text-amber-600" />}{g.canal} · {g.resultado}</span>
                        <span className="text-slate-400">{fdate(g.fecha)} · {g.gestor}</span>
                      </div>
                      {g.promesa_fecha && <p className="mt-0.5 text-slate-500">Promesa: {fdate(g.promesa_fecha)} por {mxn(g.promesa_monto)}</p>}
                      {g.notas && <p className="mt-0.5 text-slate-500">{g.notas}</p>}
                      {g.cargo_cobranza && (
                        <p className="mt-1 inline-flex items-center gap-1 rounded bg-orange-100 px-2 py-0.5 text-[11px] font-medium text-orange-800"
                           data-testid={`gestion-cargo-badge-${g.id}`}>
                          <Receipt className="h-3 w-3" />
                          Cargo aplicado: {mxn(g.cargo_cobranza.monto)}
                          {g.cargo_cobranza.iva > 0 && ` + IVA ${mxn(g.cargo_cobranza.iva)}`}
                          {g.cargo_cobranza.override && <span className="rounded bg-red-100 px-1 text-red-700">override</span>}
                        </p>
                      )}
                      {g.cargo_cobranza_error && (
                        <p className="mt-1 inline-flex items-center gap-1 rounded bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700">
                          <AlertTriangle className="h-3 w-3" /> Cargo NO aplicado: {g.cargo_cobranza_error}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo de reversión de cargo GC (Bloque 7) */}
      <Dialog open={!!reversionOpen} onOpenChange={(o) => !o && setReversionOpen(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading">
              <Undo2 className="h-5 w-5 text-red-600" /> Revertir cargo por gastos de cobranza
            </DialogTitle>
            <DialogDescription>
              Se generará una póliza inversa. El cargo queda marcado como revertido y no se cobrará al cliente.
            </DialogDescription>
          </DialogHeader>
          {reversionOpen?.cargo && (
            <div className="space-y-3">
              <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-xs">
                <p><b>Fecha:</b> {fdate(reversionOpen.cargo.fecha)}</p>
                <p><b>Monto:</b> {mxn(reversionOpen.cargo.monto)} {reversionOpen.cargo.iva > 0 && `+ IVA ${mxn(reversionOpen.cargo.iva)}`}</p>
                <p><b>Gestor:</b> {reversionOpen.cargo.gestor || "—"}</p>
                <p><b>Canal/Resultado:</b> {reversionOpen.cargo.canal}/{reversionOpen.cargo.resultado}</p>
              </div>
              <div>
                <Label className="mb-1 block text-xs">Motivo de la reversión (obligatorio, mínimo 4 caracteres)</Label>
                <Input
                  data-testid="cobranza-gc-revertir-motivo"
                  value={reversionOpen.motivo}
                  onChange={(e) => setReversionOpen({ ...reversionOpen, motivo: e.target.value })}
                  placeholder="Detalle auditable de la reversión"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setReversionOpen(null)} disabled={busy}>Cancelar</Button>
                <Button
                  data-testid="cobranza-gc-revertir-confirmar"
                  variant="destructive" size="sm" onClick={revertirCargo} disabled={busy}
                >
                  {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Undo2 className="mr-1.5 h-3.5 w-3.5" />}
                  Revertir cargo
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
